import { Flame } from "lucide-react";

function VegMark({ veg }) {
  return (
    <span
      className={`inline-flex w-3 h-3 items-center justify-center border rounded-[2px] shrink-0 mt-1 ${
        veg ? "border-green-500" : "border-red-500"
      }`}
      title={veg ? "Vegetarian" : "Non-vegetarian"}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          veg ? "bg-green-500" : "bg-red-500"
        }`}
      ></span>
    </span>
  );
}

function SpiceMark({ level }) {
  if (!level) return null;
  return (
    <span
      data-testid="spice-mark"
      className="inline-flex items-center gap-0.5 align-middle"
      title={level === 2 ? "Extra spicy" : "Medium spicy"}
    >
      {Array.from({ length: level }).map((_, i) => (
        <Flame
          key={i}
          size={12}
          className={level === 2 ? "text-red-500" : "text-orange-400"}
          fill="currentColor"
        />
      ))}
    </span>
  );
}

function ItemRow({ item }) {
  return (
    <li
      data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="flex items-baseline gap-2 pb-2.5 border-b border-[#1E313D]/60 last:border-0 last:pb-0"
    >
      <VegMark veg={item.veg} />
      <span className="min-w-0">
        <span className="text-[15px] font-medium text-[#F3EAD8]">
          {item.name}
        </span>{" "}
        <SpiceMark level={item.spice} />
        {item.meta && (
          <span className="block text-[11px] text-[#A8B8C0]">{item.meta}</span>
        )}
      </span>
      <span
        aria-hidden="true"
        className="flex-1 border-b border-dotted border-[#2A3F4D] mx-1 -translate-y-1"
      ></span>
      <span className="text-[15px] text-[#F7D774] font-semibold whitespace-nowrap">
        {item.prices.join(" / ")}
      </span>
    </li>
  );
}

function GroupCard({ group, categorySlug }) {
  return (
    <div
      data-testid={`menu-group-${categorySlug}-${group.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-[#111D25] p-6 rounded-lg border border-[#1E313D] hover:border-[#D4AF37]/50 transition-colors"
    >
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[#1E313D]">
        <h3 className="font-display font-bold text-lg text-[#F7D774]">
          {group.name}
        </h3>
        {group.columns && (
          <span className="text-xs text-[#A8B8C0]">
            {group.columns.join(" / ")}
          </span>
        )}
      </div>
      <ul className="space-y-3">
        {group.items.map((item) => (
          <ItemRow key={item.name} item={item} />
        ))}
      </ul>
    </div>
  );
}

export default function MenuSection({ category }) {
  const cols =
    category.groups.length >= 3
      ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
      : "grid-cols-1 md:grid-cols-2";

  return (
    <section
      id={category.slug}
      data-testid={`menu-section-${category.slug}`}
      className="py-16 px-6 max-w-7xl mx-auto border-t border-[#1E313D] scroll-mt-36"
    >
      {category.layout === "split" && (
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 pb-4 border-b border-[#1E313D]">
          <div>
            <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">
              {category.eyebrow}
            </span>
            <h2 className="font-display text-3xl font-bold text-[#F3EAD8] mt-1">
              {category.title}
            </h2>
          </div>
          {category.note && (
            <span className="text-sm text-[#A8B8C0] mt-2 md:mt-0">
              {category.note}
            </span>
          )}
        </div>
      )}
      {category.layout === "center" && (
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">
            {category.eyebrow}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#F3EAD8] mt-1">
            {category.title}
          </h2>
          {category.note && (
            <p className="text-sm text-[#A8B8C0] mt-2">{category.note}</p>
          )}
        </div>
      )}
      <div className={`grid ${cols} gap-6 items-start`}>
        {category.groups.map((group) => (
          <GroupCard
            key={group.name}
            group={group}
            categorySlug={category.slug}
          />
        ))}
      </div>
    </section>
  );
}
